import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
} from "recharts";
import { SlidersHorizontal, TrendingUp, TrendingDown, DollarSign, Plane, BarChart3, Users } from "lucide-react";

const COLORS = { revenue: "#10b981", expenses: "#ef4444", profit: "#0ea5e9", neutral: "#64748b" };

export default function WhatIfSimulator({ team }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [params, setParams] = useState({
    fare_strategy: "fixed",
    marketing_budget: 0,
    maintenance_spending: 150000,
    maintenance_strategy: "reactive",
    salary_level: "market",
    training_investment: 0,
    fuel_hedging: 0,
    catering_level: "basic",
    seat_comfort: "standard",
    inflight_entertainment: "none",
    discount_level: 0,
    loyalty_program: "none",
  });

  const update = (key, val) => setParams(p => ({ ...p, [key]: val }));

  const calculate = useCallback(async () => {
    if (!team?.id) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/teams/${team.id}/what-if`, params);
      setResult(res.data);
    } catch {
      toast.error("Projection failed");
    } finally {
      setLoading(false);
    }
  }, [team?.id, params]);

  useEffect(() => {
    const timer = setTimeout(() => { if (team?.id) calculate(); }, 600);
    return () => clearTimeout(timer);
  }, [calculate, team?.id]);

  const kpis = result ? [
    { label: t("whatIf.revenue"), value: result.projected_revenue, icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: t("whatIf.expenses"), value: result.projected_expenses, icon: TrendingDown, color: "text-rose-600", bg: "bg-rose-50" },
    { label: t("whatIf.profit"), value: result.projected_profit, icon: result.projected_profit >= 0 ? TrendingUp : TrendingDown, color: result.projected_profit >= 0 ? "text-emerald-600" : "text-rose-600", bg: result.projected_profit >= 0 ? "bg-emerald-50" : "bg-rose-50" },
    { label: t("whatIf.loadFactor"), value: result.projected_load_factor, icon: Plane, color: "text-sky-600", bg: "bg-sky-50", pct: true },
    { label: t("whatIf.stockPrice"), value: result.projected_stock_price, icon: BarChart3, color: "text-violet-600", bg: "bg-violet-50", dollar: true },
    { label: t("whatIf.cash"), value: result.projected_cash, icon: DollarSign, color: "text-amber-600", bg: "bg-amber-50" },
  ] : [];

  const costData = result ? Object.entries(result.breakdown?.costs || {}).map(([k, v]) => ({
    name: k.replace(/_/g, " "), value: Math.round(v),
  })) : [];

  const radarData = result ? [
    { metric: "Quality", value: result.ratings?.quality || 0, max: 100 },
    { metric: "Reliability", value: result.ratings?.reliability || 0, max: 100 },
    { metric: "Brand", value: result.ratings?.brand_awareness || 0, max: 100 },
    { metric: "Employee Sat.", value: result.ratings?.employee_satisfaction || 0, max: 100 },
    { metric: "Customer Sat.", value: result.ratings?.customer_satisfaction || 0, max: 100 },
  ] : [];

  return (
    <div className="space-y-6" data-testid="what-if-simulator">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-sky-600" />
            {t("whatIf.title")}
          </h3>
          <p className="text-sm text-slate-500">{t("whatIf.subtitle")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sliders Panel */}
        <Card className="lg:col-span-1">
          <CardHeader className="card-header-flight-deck py-3">
            <CardTitle className="text-sm">Variables</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5 max-h-[600px] overflow-y-auto">
            <SliderRow label={t("whatIf.fareStrategy")} type="select" value={params.fare_strategy}
              options={[["fixed","Fixed"],["dynamic","Dynamic"]]}
              onChange={v => update("fare_strategy", v)} />

            <SliderRow label={t("whatIf.marketingBudget")} value={params.marketing_budget}
              min={0} max={500000} step={10000}
              format={v => formatCurrency(v)}
              onChange={v => update("marketing_budget", v)} />

            <SliderRow label={t("whatIf.maintenanceSpending")} value={params.maintenance_spending}
              min={50000} max={1000000} step={25000}
              format={v => formatCurrency(v)}
              onChange={v => update("maintenance_spending", v)} />

            <SliderRow label={t("whatIf.maintenanceStrategy")} type="select" value={params.maintenance_strategy}
              options={[["reactive","Reactive"],["preventive","Preventive"],["predictive","Predictive"]]}
              onChange={v => update("maintenance_strategy", v)} />

            <SliderRow label={t("whatIf.salaryLevel")} type="select" value={params.salary_level}
              options={[["below_20","Below 20%"],["market","Market"],["above_10","Above 10%"],["above_20","Above 20%"]]}
              onChange={v => update("salary_level", v)} />

            <SliderRow label={t("whatIf.trainingInvestment")} value={params.training_investment}
              min={0} max={500000} step={10000}
              format={v => formatCurrency(v)}
              onChange={v => update("training_investment", v)} />

            <SliderRow label={t("whatIf.fuelHedging")} value={params.fuel_hedging}
              min={0} max={100} step={10}
              format={v => `${v}%`}
              onChange={v => update("fuel_hedging", v)} />

            <SliderRow label={t("whatIf.discountLevel")} value={params.discount_level}
              min={0} max={40} step={5}
              format={v => `${v}%`}
              onChange={v => update("discount_level", v)} />

            <SliderRow label={t("whatIf.cateringLevel")} type="select" value={params.catering_level}
              options={[["basic","Basic"],["standard","Standard"],["premium","Premium"]]}
              onChange={v => update("catering_level", v)} />

            <SliderRow label={t("whatIf.seatComfort")} type="select" value={params.seat_comfort}
              options={[["standard","Standard"],["enhanced","Enhanced"],["premium","Premium"]]}
              onChange={v => update("seat_comfort", v)} />

            <SliderRow label={t("whatIf.loyaltyProgram")} type="select" value={params.loyalty_program}
              options={[["none","None"],["basic","Basic"],["premium","Premium"]]}
              onChange={v => update("loyalty_program", v)} />
          </CardContent>
        </Card>

        {/* Results Panel */}
        <div className="lg:col-span-2 space-y-4">
          {result ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {kpis.map(kpi => (
                  <div key={kpi.label} className={`${kpi.bg} rounded-xl p-3 border border-slate-100`} data-testid={`whatif-kpi-${kpi.label.toLowerCase().replace(/\s/g,'-')}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{kpi.label}</span>
                    </div>
                    <p className={`font-mono font-bold text-lg ${kpi.color}`}>
                      {kpi.pct ? `${kpi.value}%` : kpi.dollar ? `$${kpi.value.toFixed(2)}` : formatCurrency(kpi.value)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="py-3 border-b border-slate-100">
                    <CardTitle className="text-sm">{t("whatIf.costBreakdown")}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={costData} layout="vertical" margin={{ left: 70, right: 10 }}>
                        <XAxis type="number" tickFormatter={v => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={65} />
                        <Tooltip formatter={v => formatCurrency(v)} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {costData.map((_, i) => <Cell key={`c-${i}`} fill={["#0ea5e9","#f59e0b","#8b5cf6","#ef4444","#10b981","#64748b"][i % 6]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="py-3 border-b border-slate-100">
                    <CardTitle className="text-sm">{t("whatIf.ratingsImpact")}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#e2e8f0" />
                        <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: "#64748b" }} />
                        <Radar dataKey="value" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.25} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card className="flex items-center justify-center h-64">
              <CardContent className="text-center">
                <SlidersHorizontal className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Adjust the sliders to see projections</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, format, onChange, type, options }) {
  if (type === "select") {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-600">{label}</label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map(([val, lbl]) => (
              <SelectItem key={val} value={val}>{lbl}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="flex justify-between">
        <label className="text-xs font-medium text-slate-600">{label}</label>
        <span className="text-xs font-mono text-sky-600">{format ? format(value) : value}</span>
      </div>
      <Slider value={[value]} onValueChange={([v]) => onChange(v)} min={min} max={max} step={step} className="py-1" />
    </div>
  );
}
